/**
 * WHO-5531. Hand-written mirror of the `accountDashboard` gateway contract.
 *
 * These types are NOT generated from `src/Apollo/schema.ts` on purpose: the
 * gateway query is being built in parallel and is not deployed yet, so it
 * cannot appear in codegen. See dashboard-client.ts for the full rationale.
 * When the gateway ships, replace this file with the generated types.
 */

export type AccountActionType =
  | "LABEL_FAILED"
  | "STRIPE_VERIFICATION"
  | "RESPOND_TO_BID"
  | "DOWNLOAD_LABEL"
  | "CONFIRM_RECEIPT"
  | "CURATION_CHANGES_REQUESTED"
  | "LISTINGS_EXPIRING"
  | "UNANSWERED_MESSAGE"
  | "WRITE_REVIEW"
  /** An accepted Open Home offer: the owner adds measurements and delivery so the buyer can pay. */
  | "OPEN_HOME_ADD_DETAILS";

export type AccountActionPriority = "BLOCKER" | "TIME_BOUND" | "REGULAR";

export type PostalLabelFailureCategory =
  | "WEIGHT"
  | "DIMENSIONS"
  | "ADDRESS"
  | "SERVICE_POINT"
  | "CARRIER"
  | "UNKNOWN";

export type PostalLabelFailure = {
  category: PostalLabelFailureCategory;
  message: string | null;
  failedAt: string | null;
};

export type AccountAction = {
  id: string;
  type: AccountActionType;
  priority: AccountActionPriority;
  title: string | null;
  /** EUR. */
  amount: number | null;
  orderNumber: string | null;
  count: number | null;
  /** ISO 8601. */
  deadline: string | null;
  /** ISO 8601. */
  occurredAt: string;
  orderId: string | null;
  /**
   * Order GROUP id. The buyer-side purchase page
   * (`pages/account/purchases/[id].tsx`) is keyed on this, not on `orderId`.
   */
  orderGroupId: string | null;
  productId: string | null;
  threadId: string | null;
  bidId: string | null;
  labelFailure: PostalLabelFailure | null;
  /** DOWNLOAD_LABEL rows: the hosted label, downloaded in place (F1). */
  labelUrl: string | null;
};

export type AccountKpis = {
  activeAdsCount: number;
  openBidsCount: number;
  /** EUR, after commission. */
  expectedPayout: number;
  soldThisYearCount: number;
};

export type AccountActivityType =
  | "ORDER_PLACED"
  /** Buyer side of an order; amount is the price paid (WHO-5531 round 4, F12). */
  | "PURCHASE_PLACED"
  | "ORDER_SHIPPED"
  | "ORDER_DELIVERED"
  | "ORDER_COMPLETED"
  | "PAYOUT_SENT"
  | "LISTING_PUBLISHED"
  | "LISTING_SOLD"
  | "LISTING_EXPIRED"
  | "BID_ACCEPTED"
  | "BID_REJECTED"
  /** Members saved one of the merchant's listings; `count` is how many. */
  | "LISTING_FAVORITED"
  /** A listing the merchant saved was sold to someone else. */
  | "FAVORITE_SOLD"
  /** A listing the merchant saved got cheaper; `amount` is the new price. */
  | "FAVORITE_PRICE_LOWERED";

export type AccountActivity = {
  id: string;
  type: AccountActivityType;
  /** ISO 8601. */
  occurredAt: string;
  title: string | null;
  /** EUR. */
  amount: number | null;
  orderNumber: string | null;
  orderId: string | null;
  /** Order GROUP id, see `AccountAction.orderGroupId`. */
  orderGroupId: string | null;
  productId: string | null;
  /** LISTING_FAVORITED: how many members saved it. Absent on a gateway without it. */
  count?: number | null;
  /** FAVORITE_PRICE_LOWERED: the price before the drop. */
  previousAmount?: number | null;
};

export type AccountDashboard = {
  openActions: AccountAction[];
  kpis: AccountKpis;
  recentActivity: AccountActivity[];
  hasListings: boolean;
  hasPurchases: boolean;
};

/**
 * WHO-5981. The blocks the gateway resolves on their own. A block that failed
 * comes back `null` with an error whose path names it; the others still load.
 */
export type DashboardBlock = "openActions" | "kpis" | "recentActivity";

export const DASHBOARD_BLOCKS: readonly DashboardBlock[] = [
  "openActions",
  "kpis",
  "recentActivity",
];

/** What the gateway sends: every block nullable since WHO-5981. */
export type AccountDashboardPayload = Omit<AccountDashboard, DashboardBlock> & {
  openActions: AccountAction[] | null;
  kpis: AccountKpis | null;
  recentActivity: AccountActivity[] | null;
};

export type AccountDashboardQueryData = {
  accountDashboard: AccountDashboardPayload | null;
};

export type RetryPostalShipmentLabelData = {
  retryPostalShipmentLabel: {
    labelUrl: string | null;
    failedToSend: boolean | null;
    labelFailure: PostalLabelFailure | null;
  };
};

/**
 * Why a request did not produce data. Exhaustive and typed so the UI never
 * treats a partial payload as trusted.
 */
export type DashboardUnavailableReason =
  | "signed_out"
  | "forbidden"
  | "schema_missing"
  | "transport";
